import React, { useState } from 'react';
import { Headphones, Play, ExternalLink, Sparkles, Music, Volume2 } from 'lucide-react';
import ContentAccordion from '../components/common/ContentAccordion';
import { retreatContent } from '../data/retreatContent';

const weekThemes = {
  1: { color: 'rose', gradient: 'from-rose-500 to-rose-600', bg: 'from-rose-50 to-rose-100', border: 'border-rose-200', text: 'text-rose-700' },
  2: { color: 'amber', gradient: 'from-amber-500 to-amber-600', bg: 'from-amber-50 to-amber-100', border: 'border-amber-200', text: 'text-amber-700' },
  3: { color: 'teal', gradient: 'from-teal-500 to-teal-600', bg: 'from-teal-50 to-teal-100', border: 'border-teal-200', text: 'text-teal-700' },
  4: { color: 'violet', gradient: 'from-violet-500 to-violet-600', bg: 'from-violet-50 to-violet-100', border: 'border-violet-200', text: 'text-violet-700' }
};

const MusicTrackItem = ({ track, weekNum }) => {
  const url = `https://www.youtube.com/watch?v=${track.youtubeId}`;
  const theme = weekThemes[weekNum];
  
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`flex items-center gap-3 p-3 bg-gradient-to-r ${theme.bg} rounded-xl ${theme.border} border hover:shadow-md transition-all`}
    >
      <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${theme.gradient} flex items-center justify-center flex-shrink-0 shadow`}>
        <Play className="w-5 h-5 text-white ml-0.5" />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-gray-800 truncate">{track.title}</h4>
        <p className={`text-sm ${theme.text} font-medium`}>{track.artist}</p>
        <span className="text-xs text-gray-500">{track.genre}</span>
      </div>
      <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0" />
    </a>
  );
};

const MusicPage = () => {
  const [expandedWeek, setExpandedWeek] = useState(1);
  
  return (
    <div className="px-4 py-6 max-w-lg mx-auto space-y-6">
      {/* Header with Gradient */}
      <div className="bg-gradient-to-br from-[var(--primary)] via-[var(--primary)] to-[var(--primary-dark)] rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center justify-center mb-4">
          <div className="relative">
            <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center">
              <Headphones className="w-10 h-10" />
            </div>
            <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-[var(--secondary)] flex items-center justify-center">
              <Volume2 className="w-4 h-4" />
            </div>
          </div>
        </div>
        <h1 className="font-heading text-2xl font-bold text-center mb-1">
          {retreatContent.musicForPrayer.title}
        </h1>
        <p className="text-white/80 text-sm text-center">{retreatContent.musicForPrayer.subtitle}</p>
        <div className="flex items-center justify-center gap-2 mt-4 text-white/60 text-xs">
          <Music className="w-3 h-3" />
          <span>24 Sacred Tracks • 4 Themes</span>
        </div>
      </div>

      {/* Intro Card */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-gray-700 text-sm leading-relaxed">
            {retreatContent.musicForPrayer.intro}
          </p>
        </div>
      </div>

      {/* Weeks */}
      <div className="space-y-4">
        {retreatContent.musicForPrayer.weeks.map((weekData) => {
          const theme = weekThemes[weekData.weekNumber];
          return (
            <div key={weekData.weekNumber} className="space-y-2">
              <div className={`flex items-center gap-2 px-1`}>
                <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${theme.gradient}`} />
                <span className={`text-xs font-bold uppercase tracking-wider ${theme.text}`}>
                  Week {weekData.weekNumber}
                </span>
              </div>
              <ContentAccordion
                title={weekData.theme}
                summary={weekData.description}
                defaultOpen={weekData.weekNumber === expandedWeek}
              >
                <div className="space-y-2">
                  {weekData.tracks.map((track, idx) => (
                    <MusicTrackItem key={idx} track={track} weekNum={weekData.weekNumber} />
                  ))}
                </div>
              </ContentAccordion>
            </div>
          );
        })}
      </div>

      {/* Tips Section */}
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-5 border border-indigo-200">
        <h3 className="font-heading font-semibold text-indigo-900 mb-3 flex items-center gap-2">
          <Headphones className="w-5 h-5" />
          Tips for Prayer with Music
        </h3>
        <ul className="space-y-2 text-sm text-indigo-800">
          <li className="flex items-start gap-2">
            <span className="text-indigo-500">•</span>
            Use headphones for a more immersive experience
          </li>
          <li className="flex items-start gap-2">
            <span className="text-indigo-500">•</span>
            Let the music help you enter into stillness
          </li>
          <li className="flex items-start gap-2">
            <span className="text-indigo-500">•</span>
            Allow the Holy Spirit to guide your meditation
          </li>
          <li className="flex items-start gap-2">
            <span className="text-indigo-500">•</span>
            Don't feel pressured to finish—rest where God invites
          </li>
        </ul>
      </div>

      {/* Footer */}
      <div className="text-center pt-2">
        <p className="text-xs text-gray-400 italic">
          "He who sings prays twice." — St. Augustine
        </p>
      </div>
    </div>
  );
};

export default MusicPage;
